/**
 * Comments Page for Accountant Portal
 * Shows all comments made by the accountant across reports
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import AccountantLayout from '../components/AccountantLayout';
import axios from '@/lib/http';
import {
  MessageSquare,
  Search,
  Filter,
  Calendar,
  FileText,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Comment {
  id: string;
  report_id: string;
  report_name: string;
  client_name: string;
  content: string;
  created_at: string;
  section?: string;
  status?: 'pending' | 'resolved' | 'acknowledged';
}

export default function AccountantComments() {
  const navigate = useNavigate();
  const { token, accountant, loading: authLoading } = useAccountantAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (authLoading) return;
    if (token) {
      fetchComments();
    }
  }, [token, authLoading]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      // Try to fetch comments - this endpoint may need to be created
      const response = await axios.get(`${BACKEND_URL}/api/v1/collaboration/accountant/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (err: any) {
      console.error('Error fetching comments:', err);
      // If endpoint doesn't exist yet, show empty state instead of error
      if (err.response?.status === 404) {
        setComments([]);
      } else {
        setError(err.response?.data?.detail || 'Failed to load comments');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const filteredComments = comments.filter(comment => {
    const matchesSearch = 
      comment.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.report_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || comment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />;
      case 'acknowledged':
        return <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />;
      default:
        return <Clock className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />;
    }
  };

  const getStatusBadge = (status?: string): React.CSSProperties => {
    const styles: Record<string, React.CSSProperties> = {
      resolved: { background: 'transparent', color: 'var(--color-verified)' },
      acknowledged: { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' },
      pending: { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
    };
    return styles[status || 'pending'] || styles.pending;
  };

  if (!authLoading && !accountant) {
    navigate('/accountant/login');
    return null;
  }

  return (
    <AccountantLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="cs-display">My Comments</h1>
            <p className="cs-meta mt-1">Comments and notes across all reports</p>
          </div>
          <button
            onClick={fetchComments}
            className="cs-chip inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors"
            style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border p-4 mb-6" style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
              <input
                type="text"
                placeholder="Search comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="cs-body w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="cs-body px-3 py-2 border rounded-lg focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="rounded-xl border p-12 text-center" style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
            <p className="cs-meta">Loading comments...</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border p-6 text-center" style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
            <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--color-brand)' }} />
            <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>{error}</p>
            <button
              onClick={fetchComments}
              className="cs-chip mt-4 px-4 py-2 rounded-lg transition-colors"
              style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-needs-human-tint)'; }}
            >
              Try Again
            </button>
          </div>
        ) : filteredComments.length === 0 ? (
          <div className="rounded-xl border p-12 text-center" style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <MessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <h3 className="cs-h2 mb-2">No Comments Yet</h3>
            <p className="cs-meta mb-4">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Your comments on reports will appear here'}
            </p>
            <button
              onClick={() => navigate('/accountant/reports')}
              className="cs-chip inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              <FileText className="w-4 h-4" />
              View Reports
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredComments.map((comment) => (
              <div
                key={comment.id}
                className="rounded-xl border p-5 transition-shadow cursor-pointer"
                style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                onClick={() => navigate(`/accountant/reports/${comment.report_id}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-needs-human-tint)' }}>
                      <MessageSquare className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{comment.report_name || 'Report'}</p>
                        {comment.section && (
                          <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
                            {comment.section}
                          </span>
                        )}
                      </div>
                      <p className="cs-body mb-2 line-clamp-2">{comment.content}</p>
                      <div className="cs-meta flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(comment.created_at)}
                        </span>
                        <span>{comment.client_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="cs-chip inline-flex items-center gap-1 px-2 py-1 rounded-full" style={getStatusBadge(comment.status)}>
                      {getStatusIcon(comment.status)}
                      {comment.status || 'pending'}
                    </span>
                    <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {!loading && filteredComments.length > 0 && (
          <div className="cs-meta mt-4 text-center">
            Showing {filteredComments.length} of {comments.length} comments
          </div>
        )}
      </div>
    </AccountantLayout>
  );
}
